import { watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useRuntimeConfig, useRouter } from 'nuxt/app'

import { useAuthStore } from '@common/auth-layer/store/auth.store'
import { useGlobalStore } from '@common/core-layer/stores/global.store'

import { useLocationStore } from '@common/location-layer/store/location.store'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

import { fcmServiceWorkerPath, isFcmServiceWorkerScriptUrl } from '../constants/fcm-service-worker'
import { PushNotificationsService } from '../services/push-notifications.service'
import { resolvePushNotificationNavigation } from '../utils/push-notification-redirect.utils'
import { usePushNotificationsStore } from '../store/push-notifications.store'
import {
  canAttemptAuthenticatedPushRegistration,
  isWebPushSupportedInBrowser,
  resolveSessionKeyForPush,
  shouldReuseCachedDeviceFcmToken,
  updateRegistrationStateAfterOutcome,
  type RegistrationState,
} from '../utils/push-notifications.utils'
import type { PushRegisterOutcome } from '../types/push-notifications.types'
import type { Messaging } from 'firebase/messaging'

/** Unsubscribe from the deferred `locationId` watch when the plugin disposes. */
let stopPendingLocationWatch: (() => void) | null = null

/** Unsubscribe from modular `onMessage` when plugin disposes or handler is re-registered */
let stopForegroundMessageListener: (() => void) | null = null

function stopPendingRegistrationWatch(): void {
  stopPendingLocationWatch?.()
  stopPendingLocationWatch = null
}

/** Clear deferred registration state on logout (preserves SW bridge + foreground handler). */
export function cleanupPushNotificationsOnLogout(): void {
  usePushNotificationsStore().reset()
  stopPendingRegistrationWatch()
}

function stopForegroundListener(): void {
  stopForegroundMessageListener?.()
  stopForegroundMessageListener = null
}

/**
 * Composable for Firebase Cloud Messaging push notifications.
 *
 * Ported from @ghl/clientportal-core with these responsibilities:
 *  - Request browser notification permission
 *  - Obtain an FCM token via Firebase Messaging v9+ SDK
 *  - Register the token with the backend
 *  - Handle foreground messages (show system notification via SW)
 *  - Bridge SW → app: listen for `redirect-from-notificationclick` messages
 *    and navigate the app (push payloads are separate from in-app notifications)
 */
export function usePushNotifications() {
  const runtimeConfig = useRuntimeConfig()
  const router = useRouter()
  const { CPNavigateTo } = useCPNavigation()
  const globalStore = useGlobalStore()
  const pushStore = usePushNotificationsStore()

  /**
   * True when `runtimeConfig.public.firebaseMessaging` has non-empty `apiKey` and `appId`
   * (supplied at runtime via `NUXT_PUBLIC_FIREBASE_MESSAGING_API_KEY` /
   * `NUXT_PUBLIC_FIREBASE_MESSAGING_APP_ID`). If either is missing, FCM must not run — avoids
   * permission prompts and Firebase init errors.
   */
  function isFirebaseMessagingConfigured(): boolean {
    const { apiKey, appId } = runtimeConfig.public.firebaseMessaging
    return Boolean(String(apiKey ?? '').trim() && String(appId ?? '').trim())
  }

  // ── Firebase Messaging (lazy, singleton) ──────────────────────────────────
  let _messagingInstance: Messaging | null = null

  async function _getMessaging(): Promise<Messaging | null> {
    if (!isFirebaseMessagingConfigured()) return null
    if (_messagingInstance) return _messagingInstance

    try {
      const { initializeApp, getApps } = await import('firebase/app')
      const { getInstallations } = await import('firebase/installations')
      const { getMessaging } = await import('firebase/messaging')

      const { apiKey, authDomain, projectId, messagingSenderId, appId } = runtimeConfig.public.firebaseMessaging

      const firebaseApp = getApps().length ? getApps()[0] : initializeApp({ apiKey, authDomain, projectId, messagingSenderId, appId })

      // Ensure Installations gets registered before Messaging asks for
      // `installations-internal`; this avoids runtime errors in some optimized bundles.
      getInstallations(firebaseApp)
      _messagingInstance = getMessaging(firebaseApp)
      return _messagingInstance
    } catch (err) {
      console.error('[FCM] Failed to initialise Firebase Messaging:', err)
      return null
    }
  }

  function _fcmServiceWorkerRegistration(reg: ServiceWorkerRegistration): boolean {
    const urls = [reg.active?.scriptURL, reg.waiting?.scriptURL, reg.installing?.scriptURL]
    return urls.some(isFcmServiceWorkerScriptUrl)
  }

  function _findFcmServiceWorkerRegistration(registrations: readonly ServiceWorkerRegistration[]): ServiceWorkerRegistration | undefined {
    return registrations.find(_fcmServiceWorkerRegistration)
  }

  async function _ensureFcmServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
    const registrations = await navigator.serviceWorker.getRegistrations()
    const existing = _findFcmServiceWorkerRegistration(registrations)
    if (existing) return existing

    try {
      const registration = await navigator.serviceWorker.register(fcmServiceWorkerPath(), { scope: '/' })
      await navigator.serviceWorker.ready
      return registration
    } catch (err) {
      console.error('[FCM] Failed to register FCM service worker:', err)
      return undefined
    }
  }

  function ensurePendingRegistrationWatch(): void {
    if (stopPendingLocationWatch) return
    const locationStore = useLocationStore()
    const { locationId } = storeToRefs(locationStore)
    stopPendingLocationWatch = watch(
      locationId,
      async id => {
        const token = pushStore.pendingFcmToken
        if (!String(id ?? '').trim() || !token) return
        pushStore.setPendingFcmToken(null)
        await _registerPushToken(token)
      },
      { flush: 'post' }
    )
  }

  // ── Token → backend ───────────────────────────────────────────────────────
  async function _registerPushToken(token: string): Promise<PushRegisterOutcome> {
    try {
      const authStore = useAuthStore()
      if (!authStore.isLoggedIn) {
        console.warn('[FCM] User not authenticated — skipping push token registration')
        return 'skipped'
      }

      const sessionId = resolveSessionKeyForPush(authStore)
      if (!sessionId) {
        console.warn('[FCM] sessionId not available — skipping push token registration')
        return 'skipped'
      }

      const locationStore = useLocationStore()
      const locationId = locationStore.locationId

      if (!String(locationId ?? '').trim()) {
        console.warn('[FCM] locationId not available — deferring push token registration until location is ready')
        pushStore.setPendingFcmToken(token)
        ensurePendingRegistrationWatch()
        return 'deferred'
      }

      const service = new PushNotificationsService()
      // GoKollab web uses the standard register-push-token path (highlevel-staging FCM
      // validation + delivery). register-push-token-gokollab is for the native Flutter
      // app (v7+) and validates against the GoKollab native FCM service account.
      const appType = globalStore.isGokollab ? 'GoKollab' : 'ClientPortal'
      await service.registerPushToken({ pushToken: token, sessionId, deviceType: 'web', appType }, locationId)

      pushStore.setPendingFcmToken(null)
      stopPendingRegistrationWatch()
      return 'success'
    } catch (err) {
      console.error('[FCM] Push token registration failed:', err)
      return 'failed'
    }
  }

  async function obtainDeviceFcmToken(messaging: Messaging): Promise<string | null> {
    const { getToken } = await import('firebase/messaging')
    const swReg = await _ensureFcmServiceWorkerRegistration()
    const vapidKey = runtimeConfig.public.firebaseMessaging.webPushCertificate || undefined
    const token = await getToken(messaging, {
      ...(vapidKey ? { vapidKey } : {}),
      serviceWorkerRegistration: swReg,
    })
    return token || null
  }

  // ── Permission + token registration ───────────────────────────────────────
  /**
   * Request notification permission, obtain the FCM token and register it
   * with the backend.  Skips silently on WebKit (Safari) which does not
   * support the Web Push standard.
   */
  async function requestAndRegisterPushToken(): Promise<PushRegisterOutcome> {
    if (!isFirebaseMessagingConfigured()) return 'skipped'
    if (!isWebPushSupportedInBrowser()) return 'skipped'

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return 'skipped'

    const messaging = await _getMessaging()
    if (!messaging) return 'skipped'

    try {
      const token = await obtainDeviceFcmToken(messaging)
      if (!token) return 'skipped'

      pushStore.setCachedDeviceFcmToken(token)
      return await _registerPushToken(token)
    } catch (err) {
      console.error('[FCM] Failed to obtain FCM token:', err)
      return 'failed'
    }
  }

  // ── Foreground message handler (app is open) ───────────────────────────────
  /**
   * When the app is in the foreground Firebase does not show a system
   * notification automatically.  We display it manually via the active SW
   * so behaviour is consistent with background messages.
   */
  async function setupForegroundMessageHandler(): Promise<void> {
    if (!isFirebaseMessagingConfigured()) return

    const messaging = await _getMessaging()
    if (!messaging) return

    stopForegroundListener()

    const { onMessage } = await import('firebase/messaging')

    stopForegroundMessageListener = onMessage(messaging, async payload => {
      const title = payload.notification?.title ?? ''
      const options: NotificationOptions = {
        body: payload.notification?.body,
        data: payload.data,
      }

      const swReg = await _ensureFcmServiceWorkerRegistration()
      if (swReg) {
        await swReg.showNotification(title, options)
      }
    })
  }

  // ── SW → app bridge (notification click → in-app route) ────────────────────
  /**
   * Registers a listener for `redirect-from-notificationclick` messages posted
   * by the cp-firebase-messaging-sw.js service worker and navigates the SPA.
   *
   * Returns a cleanup function that removes the event listener — call it
   * from plugin teardown if needed.
   *
   * Conversation redirect paths resolve to the `communities-conversations-conversationId`
   * route; the `conversation-deep-link` middleware + client plugin (communities routing-layer)
   * then decide desktop floating window vs mobile route. This bridge stays domain-free.
   */
  function setupSWMessageListener(): () => void {
    if (!isFirebaseMessagingConfigured()) return () => {}
    if (!('serviceWorker' in navigator)) return () => {}

    async function onSWMessage(event: MessageEvent): Promise<void> {
      if (event.data?.action !== 'redirect-from-notificationclick') return

      const raw = event.data?.redirectPath
      const path = typeof raw === 'string' ? raw.trim() : ''
      const rawRefId = event.data?.refId
      const refId = typeof rawRefId === 'string' ? rawRefId : undefined

      const resolvedNavigation = resolvePushNotificationNavigation(path, { refId })
      if (resolvedNavigation) {
        await CPNavigateTo(resolvedNavigation)
        return
      }

      if (!path) {
        if (raw != null && raw !== '') console.warn('[FCM] Ignoring redirectPath — expected non-empty string:', raw)
        return
      }

      // Unmapped legacy paths still use raw router.push until resolver coverage expands.
      const target = path.startsWith('/') ? path : `/${path}`
      await router.push(target)
    }

    navigator.serviceWorker.addEventListener('message', onSWMessage)

    return () => {
      navigator.serviceWorker.removeEventListener('message', onSWMessage)
    }
  }

  /** Stops deferred `locationId` watch and foreground `onMessage` listener (e.g. plugin HMR / teardown). */
  function disposePushNotificationSideEffects(): void {
    stopPendingRegistrationWatch()
    stopForegroundListener()
    _messagingInstance = null
  }

  /**
   * Register push token when the user is authenticated, and watch for post-login session.
   * Returns cleanup for plugin teardown / HMR.
   */
  function setupAuthenticatedPushRegistration(): () => void {
    const authStore = useAuthStore()
    const locationStore = useLocationStore()
    const registrationState: RegistrationState = {
      registeredForCurrentSession: false,
      lastRegisteredLocationId: '',
    }

    const attemptRegister = async (): Promise<void> => {
      const sessionKey = resolveSessionKeyForPush(authStore) ?? ''
      const loc = String(locationStore.locationId ?? '').trim()

      if (
        !canAttemptAuthenticatedPushRegistration({
          loggedIn: authStore.isLoggedIn,
          sessionKey,
          locationId: loc,
          registeredForCurrentSession: registrationState.registeredForCurrentSession,
          lastRegisteredLocationId: registrationState.lastRegisteredLocationId,
        })
      ) {
        return
      }

      const cachedToken = pushStore.cachedDeviceFcmToken
      const useCachedToken = shouldReuseCachedDeviceFcmToken(cachedToken, loc, registrationState.lastRegisteredLocationId)
      registrationState.registeredForCurrentSession = true

      const outcome = useCachedToken && cachedToken ? await _registerPushToken(cachedToken) : await requestAndRegisterPushToken()

      updateRegistrationStateAfterOutcome(outcome, loc, registrationState)
    }

    const stopAuthWatch = watch(
      () => ({
        loggedIn: authStore.isLoggedIn,
        sessionKey: resolveSessionKeyForPush(authStore) ?? '',
        loc: String(locationStore.locationId ?? '').trim(),
      }),
      ({ loggedIn, sessionKey, loc }) => {
        if (!loggedIn || !sessionKey) {
          registrationState.registeredForCurrentSession = false
          registrationState.lastRegisteredLocationId = ''
          cleanupPushNotificationsOnLogout()
          return
        }

        if (loc && registrationState.lastRegisteredLocationId && loc !== registrationState.lastRegisteredLocationId) {
          registrationState.registeredForCurrentSession = false
        }

        void attemptRegister()
      },
      { flush: 'post', immediate: true }
    )

    return () => {
      stopAuthWatch()
      registrationState.registeredForCurrentSession = false
      registrationState.lastRegisteredLocationId = ''
    }
  }

  return {
    isFirebaseMessagingConfigured,
    requestAndRegisterPushToken,
    setupForegroundMessageHandler,
    setupSWMessageListener,
    setupAuthenticatedPushRegistration,
    disposePushNotificationSideEffects,
  }
}
